// ─── EggPaintingGame.tsx ──────────────────────────────────────────────────────

import React, { useState } from "react";
import { AnimatePresence, motion, type Variants } from "framer-motion";
import background from "../assets/images/background.webp";
import BackgroundImage from "../../../components/ui/BackgroundImage";
import { CONTAINER_SIZE, DEFAULT_NUM_KEYS } from "../constants";
import { useGameLogic } from "../hooks/useGameLogic";
import type { GameCallbacks, GameConfig } from "../types";
import "../animations/EggPaintingGame.css";
import Egg from "./Egg";
import EggPaintingHeader from "./EggPaintingHeader";
import KeyButton from "./KeyButton";
import Paintbrush from "./PaintBrush";
import WinScreen from "./WinScreen";
import rabbitsImg from "../assets/images/rabbits.webp";
import { useAudioEffects } from "../../../hooks/use-audio-effects";

const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};

const cardVariants: Variants = {
  hidden: { opacity: 0, scale: 0.92, y: 18 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 260, damping: 22, delay: 0.05 },
  },
  exit: {
    opacity: 0,
    scale: 0.94,
    y: 12,
    transition: { duration: 0.22, ease: "easeIn" },
  },
};

interface Props extends GameCallbacks, GameConfig {
  className?: string;

  titleWords?: { text: string; isHighlighted?: boolean }[];
  description?: string;
  hintText?: string;

  onClose?: () => void;
  onReplay?: () => void;
}

const EggPaintingGame: React.FC<Props> = ({
  className,
  onWin,
  onValidKeyPress,
  onInvalidKeyPress,
  onGameStart,
  onGameReset,
  numberOfKeys = DEFAULT_NUM_KEYS,
  allowedKeys = [],
  showProgressBar = false,
  showHint = false,
  disableClickOnKeys = true,
  titleWords = [{ text: "EASTER EGG" }, { text: "GAME", isHighlighted: true }],
  description = "A place where you can build your own style and flex it every day with your cool pants",
  hintText = "Press the highlighted keys to paint the egg",
  onClose,
  onReplay,
}) => {
  const [visible, setVisible] = useState(true);
  const { stopEffect } = useAudioEffects();
  const handleClose = () => {
    stopEffect({ inBackground: true });
    setVisible(false);
    onClose?.();
  };

  const {
    gameKeys,
    pressedKeys,
    progress,
    won,
    stroking,
    shaking,
    invalid,
    keyPositions,
    keyRefs,
    resetGame,
    handleKeyPress,
  } = useGameLogic(
    { onWin, onValidKeyPress, onInvalidKeyPress, onGameStart, onGameReset },
    { numberOfKeys, allowedKeys },
  );

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`h-screen ${className ?? ""}`}
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <motion.div
            className="fixed top-[50%] left-[50%]"
            style={{ translateX: "-50%", translateY: "-50%" }}
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <BackgroundImage
              className="w-fit !rounded-3xl overflow-hidden"
              imageSrc={background}
              imageClassName="!object-fill !rounded-3xl"
            >
              <div className="relative p-10 min-w-[876px] min-h-[636px] 1440px:min-w-[976px] 1440px:min-h-[736px] 3440px:min-w-[1200px] 3440px:min-h-[900px]">
                <EggPaintingHeader
                  titleWords={titleWords}
                  description={description}
                  onClose={handleClose}
                />

                {showHint && !won && (
                  <p
                    style={{
                      position: "absolute",
                      bottom: 32,
                      left: "50%",
                      transform: "translateX(-50%)",
                      color: "rgba(255,255,255,0.35)",
                      fontSize: 11,
                      letterSpacing: 2,
                      textTransform: "uppercase",
                      pointerEvents: "none",
                    }}
                  >
                    {hintText}
                  </p>
                )}

                <div
                  className="absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%]"
                  style={{ width: CONTAINER_SIZE, height: CONTAINER_SIZE }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%,-50%)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Egg
                      progress={progress}
                      shaking={shaking}
                      invalid={invalid}
                    />
                    <Paintbrush progress={progress} stroking={stroking} />
                  </div>

                  {gameKeys.map((key, i) =>
                    !pressedKeys.has(key) ? (
                      <KeyButton
                        key={key}
                        keyChar={key}
                        position={keyPositions[i]}
                        onClick={
                          disableClickOnKeys
                            ? undefined
                            : () => handleKeyPress(key)
                        }
                        ref={(el) => {
                          if (el) keyRefs.current[key] = el;
                        }}
                      />
                    ) : null,
                  )}
                </div>

                {showProgressBar && (
                  <div
                    style={{
                      position: "absolute",
                      bottom: 48,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: 260,
                      height: 4,
                      borderRadius: 2,
                      background: "rgba(255,255,255,0.1)",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${progress}%`,
                        background: "rgba(255,255,255,0.5)",
                        borderRadius: 2,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                )}

                {won && (
                  <WinScreen
                    onReplay={onReplay ?? resetGame}
                    onClose={handleClose}
                  />
                )}

                <img
                  src={rabbitsImg}
                  alt="rabbits"
                  className="absolute bottom-0 left-0 w-full object-cover z-[-1]"
                />
              </div>
            </BackgroundImage>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EggPaintingGame;
